import React, { useState } from "react";
import OfficalRating from "/src/helpers/components/officalRating.jsx";
import ErrataDisplay from "/src/helpers/components/errataDisplay.jsx";
import CancelledStampOverlay from "/src/helpers/components/cancelledStampOverlay.jsx";

const HomepagePropertyRow = ({ item }) => {
    const [currentImage, setCurrentImage] = useState(0);
    const isCancelled = item?.cancelled === true;
    const images = item.images || [];

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    };

    const prevImage = () => setCurrentImage((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    const nextImage = () => setCurrentImage((prev) => (prev === images.length - 1 ? 0 : prev + 1));

    return (
        <div className={`relative rounded-card shadow-card overflow-hidden ${isCancelled ? "bg-red-50 border border-red-200" : "bg-white"}`}>
            {isCancelled && <CancelledStampOverlay />}
            {/* Header */}
            <div className="px-8 pt-6">
                <div className="flex items-center gap-2 mb-1">
                    <div className="text-emp">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                    </div>
                    <span className="text-sm font-semibold text-content_2 uppercase tracking-wide">Accommodation</span>
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                    <div>
                        <h2 className="text-[1.75rem] font-bold text-primary font-poppins">{item.name}</h2>
                        <p className="text-sm text-content_2 mt-0.5">
                            {[item.resort, item.region, item.country].filter(Boolean).join(", ")}
                        </p>
                        {item.extRef && <p className="text-xs font-medium text-content_3 mt-1">Supplier Ref: {item.extRef}</p>}
                        {item.start && item.end && (
                            <p className="text-sm text-content_2 mt-1">{formatDate(item.start)} — {formatDate(item.end)}</p>
                        )}
                    </div>
                    {item.starRating > 0 && (
                        <div className="shrink-0">
                            <OfficalRating rating={item.starRating} />
                        </div>
                    )}
                </div>
            </div>

            {/* Gallery & Intro */}
            <div className="grid md:grid-cols-[1.05fr_1fr] items-center gap-8 px-8 mb-5">
                {/* Image Gallery */}
                {images.length > 0 && (
                    <div className="relative rounded-[0.875rem] overflow-hidden">
                        <img
                            src={images[currentImage].url}
                            alt={images[currentImage].name}
                            className="w-full aspect-[16/10] object-cover"
                        />
                        {images.length > 1 && (
                            <>
                                <button
                                    onClick={prevImage}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 transition cursor-pointer"
                                    aria-label="Previous image"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <button
                                    onClick={nextImage}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1.5 transition cursor-pointer"
                                    aria-label="Next image"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                                    {images.slice(0, 10).map((_, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setCurrentImage(idx)}
                                            className={`h-2 w-2 rounded-full transition ${idx === currentImage ? "bg-white" : "bg-white/50"}`}
                                            aria-label={`Go to image ${idx + 1}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Intro & Link */}
                <div className="flex flex-col justify-center">
                    {item.intro && (
                        <div className="text-base text-content_1 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.intro }} />
                    )}
                    {item.url && (
                        <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-emp hover:underline mt-3"
                        >
                            Find out more
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </a>
                    )}
                </div>
            </div>

            {/* Rooms */}
            {item.rooms && item.rooms.length > 0 && (
                <div className="px-8 pb-6">
                    <h4 className="text-[0.8125rem] font-semibold text-content_3 uppercase tracking-[0.08em] mb-3">Rooms</h4>
                    <div className="space-y-3">
                        {item.rooms.map((room, idx) => (
                            <div key={idx} className="bg-bg_2 rounded-xl px-[1.375rem] py-[1.125rem] flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                <div>
                                    <h5 className="text-base font-semibold text-primary mb-2">{room.name}</h5>
                                    {room.guests && room.guests.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {room.guests.map((guest, gIdx) => (
                                                <div key={gIdx} className="flex items-center gap-2 text-sm text-content_1">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-content_3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    <span>{guest.title} {guest.firstname} {guest.lastname}</span>
                                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                                        guest.type === "Adult" ? "bg-emp_soft text-emp_dark" :
                                                        guest.type === "Child" ? "bg-orange_light text-orange" :
                                                        "bg-ocean-100 text-ocean-700"
                                                    }`}>{guest.type}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap shrink-0">
                                    <span className="text-xs bg-emp_soft text-emp_dark font-medium px-2.5 py-1 rounded-full">{room.boardBasisName}</span>
                                    {room.viewName && (
                                        <span className="text-xs bg-ocean-100 text-ocean-700 font-medium px-2.5 py-1 rounded-full">{room.viewName}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {item.erratas && item.erratas.length > 0 && (
                <div className="px-8 pb-2">
                    <ErrataDisplay erratas={item.erratas} />
                </div>
            )}
        </div>
    );
};

export default HomepagePropertyRow;